/**
 * Shared validation schemas for Survey Creation
 *
 * Features:
 * - Generic, reusable validation logic
 * - Type-safe with strict TypeScript
 * - Preserves unique functionality per survey type
 * - Eliminates code duplication while maintaining customizability
 */

import * as yup from 'yup';

/**
 * Shared notification settings validation schema
 * Used by: SatisfactionSurvey, CustomSurvey, EventRegistrationSurvey
 *
 * Critical differences preserved:
 * - CustomSurvey: Checks for empty strings first
 * - SatisfactionSurvey: Has ratings validation (1-5 stars)
 * - EventRegistrationSurvey: Standard email validation only
 */
export const createNotificationSettingsSchema = (options?: {
	includeRatings?: boolean;
	ratingRange?: { min: number; max: number };
}) => {
	const baseSchema = {
		isEnabled: yup.boolean().optional().default(false),
		additionalRecipients: yup
			.array()
			.of(yup.string().required('Email is required').email('Please enter a valid email address'))
			.test('additionalRecipients-validation', function (value) {
				const { isEnabled } = this.parent;
				if (!isEnabled) {
					return true;
				}

				// Check for empty strings first (CustomSurvey requirement)
				const hasEmptyStrings = value.some(recipient => !recipient || recipient.trim() === '');
				if (hasEmptyStrings) {
					return this.createError({ message: 'Please fill in all email addresses' });
				}

				// Shared email validation logic
				const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
				const validEmails = value.filter(
					recipient => recipient && recipient.trim() && emailRegex.test(recipient.trim())
				);

				if (validEmails.length !== value.length) {
					return this.createError({ message: 'Please enter valid email addresses for all recipients' });
				}

				return true;
			}),
	};

	// Add ratings validation for SatisfactionSurvey
	if (options?.includeRatings) {
		const { min = 1, max = 5 } = options.ratingRange || {};
		return yup.object({
			...baseSchema,
			ratings: yup
				.array()
				.of(yup.number())
				.test('ratings-length', `Ratings must be between ${max} and ${min}`, function (val) {
					const { isEnabled } = this.parent || {};
					if (!isEnabled) {
						return true;
					}
					return val.length >= min && val.length <= max;
				}),
		});
	}

	return yup.object(baseSchema);
};

/**
 * Shared tag generation settings validation schema
 * Used by: SatisfactionSurvey, CustomSurvey, EventRegistrationSurvey
 *
 * 100% identical across all survey types - perfect for sharing
 */
export const createTagGenerationSettingsSchema = () =>
	yup.object({
		tagOnCompletion: yup.boolean().optional().default(false),
		completionTag: yup.string().test('completionTag-validation', function (value) {
			const { tagOnCompletion } = this.parent;
			if (!tagOnCompletion) {
				return true;
			}
			if (!value || value.trim() === '') {
				return this.createError({ message: 'Completion tag is required' });
			}
			return true;
		}),
		customFormFieldTagSettings: yup
			.array()
			.of(
				yup.object({
					fieldId: yup.string().required('Field ID is required'),
					shouldGenerateTag: yup.boolean().required('Should generate tag is required'),
					tagPrefix: yup.string().required('Tag prefix is required'),
				})
			)
			.optional()
			.default([]),
	});

/**
 * Shared custom form field validation factory
 * Used by: SatisfactionSurvey, CustomSurvey, EventRegistrationSurvey
 *
 * Preserves unique requirements:
 * - SatisfactionSurvey: 20 question limit, required labels
 * - CustomSurvey: Flexible validation, optional labels
 * - EventRegistrationSurvey: Standard validation, required labels
 */
export const createCustomFormFieldSchema = (options?: { questionLimit?: number; requireLabels?: boolean }) => {
	const { questionLimit, requireLabels = true } = options || {};

	const fieldSchema = yup.object({
		fieldType: yup.mixed().oneOf(Object.values(['String', 'Option', 'MultipleOptions', 'FileUpload'] as const)),
		label: requireLabels ? yup.string().required('Question label is required') : yup.string(),
		id: yup.string().optional(),
		isOptional: yup.boolean().optional(),
		isHidden: yup.boolean().optional(),
		options: yup
			.array()
			.of(yup.object({ label: yup.string(), fieldId: yup.string() }))
			.test('options-length-test', function (val) {
				const { fieldType, label } = this.parent;

				// Option/MultipleOption fields require at least 2 options
				if (
					val?.every(x => Boolean(x?.label)) &&
					val?.length >= 2 &&
					(fieldType === 'Option' || fieldType === 'MultipleOptions')
				) {
					return true;
				}

				// String fields are valid without options
				if (fieldType === 'String') {
					// CustomSurvey specific: check label requirement for String fields
					if (!requireLabels && !label) {
						return this.createError({ message: 'This field is required' });
					}
					return true;
				}

				if (fieldType === 'FileUpload') {
					return true;
				}

				return this.createError({ message: 'This field and its options are required' });
			}),
	});

	const fieldsArraySchema = yup.array().of(fieldSchema);

	// Apply question limit if specified (SatisfactionSurvey: 20, others: unlimited)
	if (questionLimit) {
		return fieldsArraySchema.max(questionLimit, `Maximum ${questionLimit} questions allowed`);
	}

	return fieldsArraySchema;
};

/**
 * Shared base survey validation schema
 * Contains common fields across all survey types
 */
export const createBaseSurveySchema = (options?: {
	requireIntro?: boolean;
	requireStartDate?: boolean;
	requireExpirationDate?: boolean;
}) => {
	const { requireIntro = false, requireStartDate = false, requireExpirationDate = false } = options || {};

	return {
		name: yup.string().required('Survey name is required'),
		startDate: requireStartDate ? yup.string().required('Start date is required') : yup.string().optional(),
		expirationDate: requireExpirationDate
			? yup.string().when('indefinitely', (indefinitely, schema) => {
					return !indefinitely ? schema.required('Expiration date is required') : schema.notRequired();
				})
			: yup.string().optional(),
		indefinitely: yup.boolean().optional().default(false),
		customFormIsDisabled: yup.boolean().optional(),
		intro: requireIntro
			? yup
					.string()
					.required('Survey intro is required')
					.test('intro-length', 'Survey intro is required', value => Boolean(value?.length))
			: yup.mixed().optional(), // Allow IRichContentEditorState for CustomSurvey
	};
};

/**
 * Factory for creating complete survey validation schemas
 * Combines base schema with survey-specific extensions
 */
export const createSurveyValidationSchema = (
	baseOptions?: Parameters<typeof createBaseSurveySchema>[0],
	extensions?: Record<string, yup.AnySchema>
) => {
	const baseSchema = createBaseSurveySchema(baseOptions);

	return yup.object({
		...baseSchema,
		...extensions,
	});
};
